import { BLEND_MODES, Container, Graphics, Sprite, Text } from "pixi.js"
import { Coordinate, TileSize } from "game-common/models"
import { BuildingLineAxis, BuildingAreaEdges } from "../../../../building_shapes"

type FootprintState = "waiting" | "valid" | "blocked" | "shortage"
interface Footprint { graphic: Graphics; coordinate: Coordinate; born: number; state: FootprintState; yOffset: number; countingAt?: number; groupId?: number }
interface Spark { graphic: Graphics; born: number; duration: number; kind: "draw" | "erase" | "built" | "snap" | "connect" }
interface ContourEdge { footprint: Footprint; x: number; y: number; dx: number; dy: number; distance: number; period: number }

/** The gold target over the cell a build would land on, at the cell's origin. */
export const drawBuildCursor = (g: Graphics): Graphics => {
    // Dark casing makes the gold target readable over bright, textured grass.
    // Inset by half the casing width to keep the marker inside its target tile.
    g.lineStyle(4, 0x18251f, 0.95)
    g.drawRect(2, 2, TileSize - 4, TileSize - 4)
    g.lineStyle(2, 0xffe7a3, 1)
    g.beginFill(0xffd166, 0.08)
    g.drawRect(2, 2, TileSize - 4, TileSize - 4)
    g.endFill()
    return g
}

/** Small arrows rising from the cursor's bottom edge: the cell sits atop the wall beneath it. */
export const drawStoreyArrows = (g: Graphics): Graphics => {
    for (const x of [TileSize * 0.25, TileSize * 0.5, TileSize * 0.75]) {
        // Straddle the edge, in the cursor's gold over a thin casing, so they read over the wall below.
        g.lineStyle(1, 0x18251f, 0.9)
        g.beginFill(0xffd166, 1)
        g.drawPolygon([x - 3.5, TileSize + 0.5, x, TileSize - 4.5, x + 3.5, TileSize + 0.5])
        g.endFill()
    }
    return g
}

/** One climb of the storey arrows: how long, and how far up the cell they rise before they are gone. */
export const STOREY_ARROW_PERIOD_MS = 1600
export const STOREY_ARROW_RISE_PX = 10

/** The arrows' rise (px, up is negative) and opacity at `now`: they climb, fade, and start again at the edge. */
export const storeyArrowPose = (now: number): { y: number; alpha: number } => {
    const t = (((now % STOREY_ARROW_PERIOD_MS) + STOREY_ARROW_PERIOD_MS) % STOREY_ARROW_PERIOD_MS) / STOREY_ARROW_PERIOD_MS
    const eased = t * (2 - t)
    const alpha = t < 0.15 ? t / 0.15 : t < 0.45 ? 1 : 1 - (t - 0.45) / 0.55
    return { y: -STOREY_ARROW_RISE_PX * eased, alpha }
}

/**
 * What the build cursor adds about storeys: arrows on its bottom edge when the wall goes atop the wall under the
 * pointer (the cursor is plain otherwise), and the storey the cell is on, tiny at its top right, for debugging.
 */
export class StoreyCursorCue {
    readonly arrows = drawStoreyArrows(new Graphics())
    readonly label = new Text("", {
        fontFamily: "Arial", fontSize: 7, fontWeight: "bold", fill: 0xffe7a3, stroke: 0x18251f, strokeThickness: 2,
    })

    constructor(cursor: Container) {
        this.arrows.visible = false
        // The cursor is in world space and the camera zooms it, so draw the glyphs dense enough to stay sharp.
        this.label.resolution = 8
        // Inside the top right corner, so the cursor still outlines exactly its cell.
        this.label.anchor.set(1, 0)
        this.label.position.set(TileSize - 4, 3)
        cursor.addChild(this.arrows, this.label)
    }

    update = (atop: boolean, level: number) => {
        this.arrows.visible = atop
        const text = String(level)
        if (this.label.text !== text) this.label.text = text
    }

    /** Called every frame: the arrows keep climbing out of the bottom edge, so the cell reads as building up. */
    animate = (now: number) => {
        if (!this.arrows.visible) return
        const { y, alpha } = storeyArrowPose(now)
        this.arrows.y = y
        this.arrows.alpha = alpha
    }
}

/** A dotted rise from a roof's ground cell (the origin) up to the plane its roof is drawn on. */
export const drawRoofLiftGuide = (g: Graphics, lift: number): Graphics => {
    g.beginFill(0xffd166, 0.95)
    for (let y = 0; y >= lift; y -= 4) g.drawCircle(0, y, 1)
    g.drawPolygon([0, lift, -4, lift + 7, 4, lift + 7])
    g.endFill()
    return g
}

/** Immediate, local feedback. Footprints express intent, never server approval. */
export class BuildPlacementFeedback {
    private root = new Container()
    private footprints = new Map<string, Footprint>()
    private sparks: Spark[] = []
    private lineEnd?: Graphics
    private eraseTarget?: Graphics
    private resizeHandle?: Graphics
    private areaFootprint?: Graphics
    private areaSignature = ""
    private areaEdges: BuildingAreaEdges = {}
    private lineAxis?: BuildingLineAxis
    private lineConnection?: string
    private lastSnapAt = -Infinity
    private current = new Graphics()
    private contour: ContourEdge[] = []
    private contourDirty = true
    private sheen = new Graphics()
    private pendingGlow = new Graphics()
    private settling: { sprite: Sprite; y: number; born: number }[] = []

    // `now` is the clock every footprint, spark and halo is timed against. The game runs on the
    // wall clock; the building tutorial poses the same effect on its own demo clock.
    constructor(private surface: () => Container, private now: () => number = () => Date.now()) {
        this.root.zIndex = 100000.25
        this.root.interactive = false
        this.root.interactiveChildren = false
        this.pendingGlow.blendMode = BLEND_MODES.ADD
    }

    add = (key: string, coordinate: Coordinate, yOffset = 0, celebrate = true) => {
        if (this.footprints.has(key)) return
        this.attach()
        const graphic = new Graphics()
        graphic.position.set(coordinate.x * TileSize, coordinate.y * TileSize + yOffset)
        this.root.addChild(graphic)
        const footprint: Footprint = { graphic, coordinate: { ...coordinate }, born: this.now(), state: "waiting", yOffset }
        this.footprints.set(key, footprint)
        this.contourDirty = true
        this.refreshContour(coordinate)
        if (celebrate) {
            this.spark(coordinate, "draw", yOffset)
            this.update()
        }
    }

    setState = (key: string, state: FootprintState) => {
        const footprint = this.footprints.get(key)
        if (!footprint || footprint.state === state) return
        footprint.state = state
        this.drawFootprint(footprint)
    }

    setCounting = (key: string, startedAt: number, groupId = startedAt) => {
        const footprint = this.footprints.get(key)
        if (footprint) { footprint.countingAt = startedAt; footprint.groupId = groupId }
    }

    pauseCounting = (key: string) => {
        const footprint = this.footprints.get(key)
        if (footprint) { footprint.countingAt = undefined; footprint.groupId = undefined }
    }

    setResizeHandles = (coordinates: Coordinate[], yOffset = 0) => {
        this.attach()
        if (!this.resizeHandle) {
            const handle = new Graphics()
            this.root.addChild(handle)
            this.resizeHandle = handle
        }
        const handle = this.resizeHandle
        handle.clear()
        handle.y = yOffset
        for (const coordinate of coordinates) {
            const x = (coordinate.x + 0.5) * TileSize, y = (coordinate.y + 0.5) * TileSize
            handle.lineStyle(1.5, 0xffe7a3, 1)
            handle.beginFill(0x18251f, 0.9)
            handle.drawPolygon([x, y - 6, x + 6, y, x, y + 6, x - 6, y])
            handle.endFill()
        }
    }

    clearResizeHandle = () => {
        this.resizeHandle?.destroy()
        this.resizeHandle = undefined
    }

    settle = (sources: Iterable<Sprite>) => {
        this.attach()
        for (const source of sources) {
            if (this.settling.length >= 48) this.settling.shift()?.sprite.destroy()
            const sprite = new Sprite(source.texture)
            sprite.position.copyFrom(source.position)
            sprite.scale.copyFrom(source.scale)
            sprite.anchor.copyFrom(source.anchor)
            sprite.pivot.copyFrom(source.pivot)
            sprite.skew.copyFrom(source.skew)
            sprite.rotation = source.rotation
            sprite.tint = source.tint
            this.root.addChild(sprite)
            this.settling.push({ sprite, y: source.y, born: this.now() })
            sprite.y -= 2
        }
    }

    setOffset = (key: string, yOffset: number) => {
        const footprint = this.footprints.get(key)
        if (!footprint || footprint.yOffset === yOffset) return
        footprint.graphic.y += yOffset - footprint.yOffset
        footprint.yOffset = yOffset
        this.contourDirty = true
        this.refreshContour(footprint.coordinate)
    }

    remove = (key: string) => {
        const footprint = this.footprints.get(key)
        footprint?.graphic.destroy()
        this.footprints.delete(key)
        this.contourDirty = true
        if (footprint) this.refreshContour(footprint.coordinate)
        if (!this.footprints.size) {
            this.current.clear()
            this.sheen.clear()
            this.pendingGlow.clear()
        }
    }

    setLineEnd = (coordinate: Coordinate, axis?: BuildingLineAxis, connection?: Coordinate, yOffset = 0) => {
        this.attach()
        const connectionKey = connection && `${connection.x},${connection.y}`
        if (!this.lineEnd) {
            this.lineEnd = new Graphics()
            this.root.addChild(this.lineEnd)
            this.drawLineEnd(axis, !!connection)
        } else if (this.lineAxis !== axis || this.lineConnection !== connectionKey) this.drawLineEnd(axis, !!connection)
        this.lineEnd.position.set(coordinate.x * TileSize, coordinate.y * TileSize + yOffset)
        const joined = connectionKey && connectionKey !== this.lineConnection
        if (joined || !connectionKey && axis && axis !== this.lineAxis && this.now() - this.lastSnapAt >= 180) {
            this.spark(coordinate, connection ? "connect" : "snap", yOffset)
            this.lastSnapAt = this.now()
        }
        this.lineAxis = axis
        this.lineConnection = connectionKey
    }

    clearLineEnd = () => {
        this.lineEnd?.destroy()
        this.lineEnd = undefined
        this.lineAxis = undefined
        this.lineConnection = undefined
        this.lastSnapAt = -Infinity
    }

    setAreaFootprint = (anchor: Coordinate, endpoint: Coordinate, edges: BuildingAreaEdges, yOffset = 0) => {
        this.attach()
        const signature = `${anchor.x},${anchor.y}:${endpoint.x},${endpoint.y}:${edges.x}:${edges.y}:${yOffset}`
        if (signature === this.areaSignature) return
        if (!this.areaFootprint) {
            this.areaFootprint = new Graphics()
            this.root.addChild(this.areaFootprint)
        }
        const graphic = this.areaFootprint
        graphic.y = yOffset
        graphic.clear()
        const left = Math.min(anchor.x, endpoint.x) * TileSize + 2
        const top = Math.min(anchor.y, endpoint.y) * TileSize + 2
        const right = (Math.max(anchor.x, endpoint.x) + 1) * TileSize - 2
        const bottom = (Math.max(anchor.y, endpoint.y) + 1) * TileSize - 2
        const movingX = endpoint.x >= anchor.x ? right : left
        const movingY = endpoint.y >= anchor.y ? bottom : top
        // The tile contour already outlines the actual cells. Keep only the anchor and snap accents here.
        graphic.lineStyle(2, 0xcaf0e4, 1)
        if (edges.x !== undefined) graphic.moveTo(movingX, top).lineTo(movingX, bottom)
        if (edges.y !== undefined) graphic.moveTo(left, movingY).lineTo(right, movingY)
        // A fixed pin identifies the starting corner while the opposite edges stretch.
        const x = (anchor.x + 0.5) * TileSize, y = (anchor.y + 0.5) * TileSize
        graphic.lineStyle(1.5, 0x18251f, 1)
        graphic.beginFill(0xffe7a3, 1)
        graphic.drawPolygon([x, y - 4, x + 4, y, x, y + 4, x - 4, y])
        graphic.endFill()
        if (edges.x !== undefined && edges.x !== this.areaEdges.x || edges.y !== undefined && edges.y !== this.areaEdges.y) {
            this.spark(endpoint, "connect", yOffset)
        }
        this.areaEdges = { ...edges }
        this.areaSignature = signature
    }

    clearAreaFootprint = () => {
        this.areaFootprint?.destroy()
        this.areaFootprint = undefined
        this.areaSignature = ""
        this.areaEdges = {}
    }

    setEraseTarget = (coordinate: Coordinate, yOffset = 0) => {
        this.attach()
        if (!this.eraseTarget) {
            const marker = new Graphics()
            marker.lineStyle(1.5, 0xb9e2e5, 0.95)
            marker.drawRoundedRect(2, 2, TileSize - 4, TileSize - 4, 3)
            const center = TileSize / 2
            marker.beginFill(0x233b40, 0.9).drawCircle(center, center, 6).endFill()
            marker.lineStyle(2, 0xd7f3f0, 1)
            marker.moveTo(center - 3, center).lineTo(center + 3, center)
            this.eraseTarget = marker
            this.root.addChild(marker)
        }
        this.eraseTarget.position.set(coordinate.x * TileSize, coordinate.y * TileSize + yOffset)
    }

    clearEraseTarget = () => {
        this.eraseTarget?.destroy()
        this.eraseTarget = undefined
    }

    private drawLineEnd = (axis?: BuildingLineAxis, connected = false) => {
        const marker = this.lineEnd!
        marker.clear()
        const ink = connected ? 0xcaf0e4 : axis ? 0xffe4a1 : 0xb9e2e5
        const edge = TileSize - 2, length = 6
        for (const [width, color] of [[4, 0x18251f], [2, ink]]) {
            marker.lineStyle(width, color, 0.95)
            for (const [x, y, dx, dy] of [[2, 2, 1, 1], [edge, 2, -1, 1], [2, edge, 1, -1], [edge, edge, -1, -1]]) {
                marker.moveTo(x + dx * length, y).lineTo(x, y).lineTo(x, y + dy * length)
            }
        }
        const center = TileSize / 2
        if (connected) {
            marker.lineStyle(3, 0x18251f, 0.95)
            marker.drawRoundedRect(center - 6, center - 3, 7, 6, 2)
            marker.drawRoundedRect(center - 1, center - 3, 7, 6, 2)
            marker.lineStyle(1, ink, 1)
            marker.drawRoundedRect(center - 6, center - 3, 7, 6, 2)
            marker.drawRoundedRect(center - 1, center - 3, 7, 6, 2)
            return
        }
        marker.lineStyle(0)
        marker.beginFill(ink, 0.95)
        marker.drawPolygon([center, center - 2, center + 2, center, center, center + 2, center - 2, center])
        marker.endFill()
    }

    spark = (coordinate: Coordinate, kind: Spark["kind"], yOffset = 0) => {
        this.attach()
        // Long strokes cannot flood the scene with particles or display objects.
        if (this.sparks.length >= 48) this.sparks.shift()?.graphic.destroy()
        const graphic = new Graphics()
        const half = TileSize / 2
        const color = kind === "erase" ? 0x9dc7c9 : kind === "connect" ? 0xcaf0e4 : 0xffd989
        graphic.lineStyle(kind === "draw" || kind === "erase" ? 1 : 1.5, color, 0.9)
        if (kind === "draw") graphic.drawPolygon([0, -3, 3, 0, 0, 3, -3, 0])
        else if (kind !== "built") graphic.drawRoundedRect(-half + 3, -half + 3, TileSize - 6, TileSize - 6, 3)
        if (kind === "built") {
            graphic.lineStyle(1, 0xfff4cf, 1)
            for (const [x, y] of [[-half + 3, -half + 3], [half - 3, -half + 3], [half - 3, half - 3]]) {
                graphic.moveTo(x - 2, y).lineTo(x + 2, y)
                graphic.moveTo(x, y - 2).lineTo(x, y + 2)
            }
        }
        graphic.position.set((coordinate.x + 0.5) * TileSize, (coordinate.y + 0.5) * TileSize + yOffset)
        this.root.addChild(graphic)
        this.sparks.push({ graphic, born: this.now(), duration: kind === "built" ? 420 : 220, kind })
    }

    update = () => {
        if (!this.footprints.size && !this.sparks.length && !this.settling.length && !this.lineEnd && !this.eraseTarget && !this.areaFootprint && !this.resizeHandle) return
        this.attach()
        const now = this.now()
        this.footprints.forEach(({ graphic, born, state, countingAt }) => {
            const arrival = Math.max(0, 1 - (now - born) / 180)
            const drawingAlpha = (state === "valid" ? 0.72 : 0.85) + arrival * 0.15
            // Once the plan pulses, its art and countdown take priority over the contour.
            const fade = countingAt !== undefined && state === "valid" ? Math.min(1, Math.max(0, (now - countingAt) / 1750)) : 0
            graphic.alpha = drawingAlpha * (1 - fade)
        })
        this.drawCurrent(now)
        this.drawPendingGlow(now)
        this.drawSheen(now)
        this.settling = this.settling.filter(entry => {
            const progress = Math.min(1, (now - entry.born) / 240)
            if (progress >= 1) { entry.sprite.destroy(); return false }
            entry.sprite.y = entry.y - 2 * (1 - progress) ** 3
            entry.sprite.alpha = 1 - progress
            return true
        })
        this.sparks = this.sparks.filter(spark => {
            const progress = Math.min(1, (now - spark.born) / spark.duration)
            if (progress >= 1) { spark.graphic.destroy(); return false }
            const eased = 1 - (1 - progress) ** 3
            spark.graphic.alpha = (1 - progress) ** 2 * 0.8
            spark.graphic.scale.set(spark.kind === "erase" ? 1 - eased * 0.18 : 0.92 + eased * 0.2)
            return true
        })
    }

    clear = () => {
        this.clearResizeHandle()
        this.clearLineEnd()
        this.clearAreaFootprint()
        this.clearEraseTarget()
        this.footprints.forEach(footprint => footprint.graphic.destroy())
        this.sparks.forEach(spark => spark.graphic.destroy())
        this.footprints.clear()
        if (!this.current.destroyed) this.current.clear()
        if (!this.sheen.destroyed) this.sheen.clear()
        if (!this.pendingGlow.destroyed) this.pendingGlow.clear()
        this.settling.forEach(entry => entry.sprite.destroy())
        this.settling = []
        this.contour = []
        this.contourDirty = true
        this.sparks = []
        this.root.parent?.removeChild(this.root)
    }

    destroy = () => {
        this.clear()
        if (!this.current.destroyed) this.current.destroy()
        if (!this.sheen.destroyed) this.sheen.destroy()
        if (!this.pendingGlow.destroyed) this.pendingGlow.destroy()
        if (!this.root.destroyed) this.root.destroy()
    }

    private attach = () => {
        const surface = this.surface()
        if (this.root.parent !== surface) surface.addChild(this.root)
    }

    private refreshContour = (coordinate: Coordinate) => {
        for (const [dx, dy] of [[0, 0], [-1, 0], [1, 0], [0, -1], [0, 1]]) {
            const footprint = this.footprints.get(`${coordinate.x + dx},${coordinate.y + dy}`)
            if (footprint) this.drawFootprint(footprint)
        }
    }

    private drawSheen = (now: number) => {
        const graphic = this.sheen
        graphic.clear()
        const groups = new Map<number, { startedAt: number; min: number; max: number; cells: Footprint[] }>()
        this.footprints.forEach(footprint => {
            if (footprint.countingAt === undefined || footprint.groupId === undefined || footprint.state !== "valid") return
            const elapsed = now - footprint.countingAt
            if (elapsed < 800 || elapsed >= 2400) return
            const origin = (footprint.coordinate.x + footprint.coordinate.y) * TileSize + footprint.yOffset
            const group = groups.get(footprint.groupId)
            if (group) { group.min = Math.min(group.min, origin); group.max = Math.max(group.max, origin + TileSize * 2); group.cells.push(footprint) }
            else groups.set(footprint.groupId, { startedAt: footprint.countingAt, min: origin, max: origin + TileSize * 2, cells: [footprint] })
        })
        if (!groups.size) return
        if (this.root.children[this.root.children.length - 1] !== graphic) this.root.addChild(graphic)
        groups.forEach(group => {
            const progress = (now - group.startedAt - 800) / 1600
            const center = group.min - 20 + (group.max - group.min + 40) * progress
            for (const footprint of group.cells) {
                const x = footprint.coordinate.x * TileSize, y = footprint.coordinate.y * TileSize + footprint.yOffset
                if (x + y > center + 20 || x + y + TileSize * 2 < center - 20) continue
                // Clip the moving light to actual cells, preserving holes and warning markers.
                for (let band = 0; band < 5; band++) {
                    const low = center - 20 + band * 8, high = low + 8
                    let polygon = [[x, y], [x + TileSize, y], [x + TileSize, y + TileSize], [x, y + TileSize]]
                    for (const [limit, sign] of [[low, 1], [high, -1]]) {
                        const clipped: number[][] = []
                        polygon.forEach((a, index) => {
                            const b = polygon[(index + 1) % polygon.length]
                            const da = (a[0] + a[1] - limit) * sign, db = (b[0] + b[1] - limit) * sign
                            if (da >= 0) clipped.push(a)
                            if ((da >= 0) !== (db >= 0)) {
                                const t = da / (da - db)
                                clipped.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t])
                            }
                        })
                        polygon = clipped
                    }
                    if (polygon.length < 3) continue
                    graphic.beginFill(0xfff4cf, [0.025, 0.05, 0.075, 0.05, 0.025][band] * Math.sin(progress * Math.PI))
                    graphic.drawPolygon(polygon.flat())
                    graphic.endFill()
                }
            }
        })
    }

    private rebuildCurrentContour = () => {
        // Clockwise edges keep the current continuous around corners and around hollow rooms.
        const edges: (ContourEdge & { start: string; end: string; direction: number })[] = []
        this.footprints.forEach(footprint => {
            const { coordinate, yOffset } = footprint
            for (const [nx, ny, x, y, dx, dy, direction] of [
                [0, -1, 0, 0, 1, 0, 0], [1, 0, 1, 0, 0, 1, 1],
                [0, 1, 1, 1, -1, 0, 2], [-1, 0, 0, 1, 0, -1, 3],
            ]) {
                if (this.footprints.get(`${coordinate.x + nx},${coordinate.y + ny}`)?.yOffset === yOffset) continue
                const vx = coordinate.x + x, vy = coordinate.y + y
                edges.push({ footprint, x: vx * TileSize, y: vy * TileSize + yOffset, dx, dy, direction,
                    start: `${yOffset}:${vx},${vy}`, end: `${yOffset}:${vx + dx},${vy + dy}`, distance: 0, period: 0 })
            }
        })
        const outgoing = new Map<string, typeof edges>()
        edges.forEach(edge => { const list = outgoing.get(edge.start) || []; list.push(edge); outgoing.set(edge.start, list) })
        const visited = new Set<ContourEdge>()
        edges.forEach(start => {
            if (visited.has(start)) return
            const loop: typeof edges = []
            let edge: typeof start | undefined = start
            while (edge && !visited.has(edge)) {
                visited.add(edge)
                edge.distance = loop.length * TileSize
                loop.push(edge)
                const direction = edge.direction
                // At a diagonal touch, turn right to follow this boundary rather than crossing it.
                edge = outgoing.get(edge.end)?.filter(next => !visited.has(next)).sort((a, b) => {
                    const turn = (value: number) => [1, 0, 3, 2].indexOf((value - direction + 4) % 4)
                    return turn(a.direction) - turn(b.direction)
                })[0]
            }
            const length = loop.length * TileSize
            const period = length / Math.max(1, Math.round(length / 160))
            loop.forEach(edge => { edge.period = period })
        })
        this.contour = edges
        this.contourDirty = false
    }

    private drawCurrent = (now: number) => {
        if (this.contourDirty) this.rebuildCurrentContour()
        const graphic = this.current
        graphic.clear()
        if (!this.contour.length) return
        // One reused overlay; only exposed edges animate, never the internal tile grid.
        if (this.root.children[this.root.children.length - 1] !== graphic) this.root.addChild(graphic)
        for (const edge of this.contour) {
            if (edge.footprint.graphic.alpha <= 0 || !["waiting", "valid"].includes(edge.footprint.state)) continue
            const phase = (now * 0.064 - edge.distance) % edge.period
            const head = (phase + edge.period) % edge.period
            for (let pulse = head - edge.period; pulse < TileSize + 12; pulse += edge.period) {
                for (let part = 0; part < 3; part++) {
                    const from = Math.max(0, pulse - 12 + part * 4)
                    const to = Math.min(TileSize, pulse - 8 + part * 4)
                    if (to <= from) continue
                    graphic.lineStyle(1.5, 0xfff4cf, edge.footprint.graphic.alpha * (part + 1) / 3)
                    graphic.moveTo(edge.x + edge.dx * from, edge.y + edge.dy * from)
                        .lineTo(edge.x + edge.dx * to, edge.y + edge.dy * to)
                }
            }
        }
    }

    private drawPendingGlow = (now: number) => {
        const graphic = this.pendingGlow
        graphic.clear()
        if (!this.contour.length) return
        if (this.root.children[this.root.children.length - 1] !== graphic) this.root.addChild(graphic)
        for (const edge of this.contour) {
            const { countingAt, state } = edge.footprint
            if (countingAt === undefined || state !== "valid") continue
            const elapsed = now - countingAt
            // A moving halo traces the plan without reinstating a full guideline or blinking the art.
            const alpha = Math.max(0, Math.min(1, elapsed / 250, (5000 - elapsed) / 350))
            if (!alpha) continue
            const phase = (now * 0.048 - edge.distance) % edge.period
            const head = (phase + edge.period) % edge.period
            for (let pulse = head - edge.period; pulse < TileSize + 24; pulse += edge.period) {
                for (let part = 0; part < 6; part++) {
                    const from = Math.max(0, pulse - 24 + part * 4)
                    const to = Math.min(TileSize, pulse - 20 + part * 4)
                    if (to <= from) continue
                    const strength = alpha * ((part + 1) / 6) ** 2
                    // Low-opacity layers form a soft halo around one bright core.
                    for (const [width, opacity] of [[7, 0.025], [4, 0.07], [1.5, 0.45]]) {
                        graphic.lineStyle(width, 0xffe5ae, strength * opacity)
                        graphic.moveTo(edge.x + edge.dx * from, edge.y + edge.dy * from)
                            .lineTo(edge.x + edge.dx * to, edge.y + edge.dy * to)
                    }
                }
            }
        }
    }

    private drawFootprint = ({ graphic, state, coordinate, yOffset }: Footprint) => {
        const color = state === "blocked" ? 0xff8174 : state === "shortage" ? 0xffc35a : 0xffe7a3
        graphic.clear()
        if (state === "waiting" || state === "valid") {
            // Connected tiles read as one silhouette, including holes and stepped edges.
            graphic.lineStyle(0)
            graphic.beginFill(color, state === "waiting" ? 0.12 : 0.04)
            graphic.drawRect(0, 0, TileSize, TileSize)
            graphic.endFill()
            const edges = [
                [-1, 0, 0, 0, 0, TileSize],
                [1, 0, TileSize, 0, TileSize, TileSize],
                [0, -1, 0, 0, TileSize, 0],
                [0, 1, 0, TileSize, TileSize, TileSize],
            ]
            graphic.lineStyle(1.5, color, 1)
            for (const [dx, dy, x1, y1, x2, y2] of edges) {
                const neighbor = this.footprints.get(`${coordinate.x + dx},${coordinate.y + dy}`)
                if (neighbor?.yOffset === yOffset) continue
                graphic.moveTo(x1, y1).lineTo(x2, y2)
            }
            return
        }
        // A dark casing separates the light edge from grass without covering the tile's art.
        for (const [width, ink] of [[3.5, 0x18251f], [1.5, color]]) {
            graphic.lineStyle(width, ink, 1)
            if (state === "shortage") {
                // Preserve the whole intended footprint even without enough material.
                for (let offset = 3; offset < TileSize - 3; offset += 6) {
                    const end = Math.min(offset + 3, TileSize - 3)
                    graphic.moveTo(offset, 3).lineTo(end, 3)
                    graphic.moveTo(offset, TileSize - 3).lineTo(end, TileSize - 3)
                    graphic.moveTo(3, offset).lineTo(3, end)
                    graphic.moveTo(TileSize - 3, offset).lineTo(TileSize - 3, end)
                }
            } else {
                graphic.drawRoundedRect(3, 3, TileSize - 6, TileSize - 6, 2)
            }
        }
        if (state !== "shortage") {
            graphic.lineStyle(0)
            graphic.beginFill(color, 0.04)
            graphic.drawRoundedRect(3, 3, TileSize - 6, TileSize - 6, 2)
            graphic.endFill()
        }
    }
}
